import getInitials from 'initials';
import { useMemo } from 'react';
import uniqolor from 'uniqolor';

import { tv } from '@eluve/components';
import {
  Avatar,
  AvatarFallback,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@eluve/components';

interface AvatarWithInitialsProps {
  /** Use to generate initials */
  name: string;
  /** Use to set unique background color to the avatar */
  id?: string;
  /** Avatar size — defaults to `lg` to preserve existing callers */
  size?: 'default' | 'sm' | 'lg';
}

const initialVariant = tv({
  base: 'text-xs font-semibold',
  variants: {
    color: {
      dark: 'text-bg-primary mix-blend-overlay',
      light: 'text-text-primary/60 mix-blend-color-burn',
    },
  },
});

export const AvatarWithInitials: React.FC<AvatarWithInitialsProps> = ({
  name,
  id,
  size = 'lg',
}) => {
  const initials = useMemo(() => getInitials(name).slice(0, 3), [name]);
  const userId = useMemo(() => id ?? name, [id, name]);
  const bgColor = useMemo(
    () => uniqolor(userId, { lightness: [50, 85] }),
    [userId],
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Avatar size={size}>
          <AvatarFallback style={{ backgroundColor: bgColor.color }}>
            <span
              className={initialVariant({
                color: bgColor.isLight ? 'light' : 'dark',
              })}
            >
              {initials}
            </span>
          </AvatarFallback>
        </Avatar>
      </TooltipTrigger>
      <TooltipContent>
        <span className="privacy-text">{name}</span>
      </TooltipContent>
    </Tooltip>
  );
};
